import { TRANSFER_METHODS } from "@tastyworks/shared-utils/misc/constants"
import { isEmpty, isNil } from "lodash-es"
import { RestResource } from "../common"
import { SchemaDeSerBuilder, type SchemaDeSer } from "../deser"
import { IRA_DETAIL_DESER, IraDetail } from "../ira-detail"
import { ArrayMap } from "../util/collection"
import { Cost, CostEffect } from "../util/cost"
import type { DateISOString } from "../util/date-iso-string"
import { ACH_RELATIONSHIP_DESER, AchRelationship } from "./ach-relationship"
import { RECURRING_DEPOSIT_DESER, RecurringDeposit } from "./recurring-deposit"
import {
  BANKING_PRE_REGISTRATION_ACCOUNT_TYPES,
  BANKING_PRE_REGISTRATION_STATUSES,
  PRE_REGISTERED_DEPOSIT_STATUSES,
  PRE_REGISTERED_DEPOSIT_TERMINAL_STATES,
  PendingCashEntryState,
  RELATIONSHIP_METHODS,
  TERMINAL_TRANSFER_STATES,
  TRANSACTION_REAUTH_REQUIRED_REASON,
  TRANSFER_DIRECTIONS,
  TRANSFER_STATES,
  TransactionStatusBucket,
  getStatusBucket,
} from "./shared"

export * from "./ach-relationship"
export * from "./recurring-deposit"
export * from "./shared"

export class WithdrawalLimit {
  permittedWithdrawalAmount = 0
  activeWithdrawalHoldAmount = 0
}

export const WITHDRAWAL_LIMIT_DESER: SchemaDeSer<WithdrawalLimit> =
  new SchemaDeSerBuilder(WithdrawalLimit)
    .ofFloat("permittedWithdrawalAmount")
    .ofFloat("activeWithdrawalHoldAmount")
    .toDeSer()

function createPreRegisteredDepositsMap(): ArrayMap<
  string,
  PreRegisteredDeposit
> {
  return ArrayMap.stringKey(PreRegisteredDeposit, "id")
}

export interface CashTransaction {
  id: string
  direction: string
  amount: number
  createdAt: Date | null
  updatedAt: Date | null
  isCancelable: boolean
  isCanceled: boolean
  isComplete: boolean
  isPresend: boolean
  isTerminal: boolean
  isDeposit: boolean
  isWithdrawal: boolean
  status: string
}
export class PreRegisteredDeposit
  extends RestResource
  implements CashTransaction
{
  static onInitialize = (_obj: PreRegisteredDeposit) => {
    /* no-op */
  }

  constructor() {
    super()

    PreRegisteredDeposit.onInitialize(this)
  }

  status = ""
  amount = 0
  bankingPreRegistrationId: number | null = null
  externalTransactionId: number | null = null
  createdAt = new Date()
  updatedAt = new Date()

  get isCancelable() {
    return this.isPending
  }

  get isCanceled(): boolean {
    return this.status === PRE_REGISTERED_DEPOSIT_STATUSES.CANCELED
  }

  get isComplete(): boolean {
    return this.isSubmitted
  }

  get isPending(): boolean {
    return (
      this.status ===
      PRE_REGISTERED_DEPOSIT_STATUSES.AWAITING_ACH_RELATIONSHIP_CREATION
    )
  }

  get isPresend() {
    return this.isPending
  }

  get isSubmitted() {
    return this.status === PRE_REGISTERED_DEPOSIT_STATUSES.SUBMITTED_TO_CLEARING
  }

  get isTerminal(): boolean {
    return PRE_REGISTERED_DEPOSIT_TERMINAL_STATES.includes(this.status)
  }

  get direction(): TRANSFER_DIRECTIONS {
    return TRANSFER_DIRECTIONS.INCOMING
  }

  readonly isDeposit = true

  readonly isWithdrawal = false
}

export class BankingPreRegistration extends RestResource {
  static onInitialize = (_obj: BankingPreRegistration) => {
    /* no-op */
  }

  constructor() {
    super()

    BankingPreRegistration.onInitialize(this)
  }

  accountOpeningApplicationId: number | null = null
  achRelationshipMethod = ""
  bankAccount = ""
  bankRoutingNumber = ""
  bankAccountType = BANKING_PRE_REGISTRATION_ACCOUNT_TYPES.CHECKING
  externalAccountId = ""
  externalAccountNumber = ""
  institutionType = ""
  nickname = ""
  publicToken = ""
  status = ""
  achRelationship: AchRelationship | null = null
  preRegisteredDeposits = createPreRegisteredDepositsMap()
  reason = ""
  createdAt = new Date()
  updatedAt = new Date()

  get isCanceled() {
    return this.status === BANKING_PRE_REGISTRATION_STATUSES.CANCELED
  }

  get isAwaitingAccountCreation() {
    return (
      this.status ===
      BANKING_PRE_REGISTRATION_STATUSES.AWAITING_ACCOUNT_CREATION
    )
  }

  get isSubmitted() {
    return (
      this.status === BANKING_PRE_REGISTRATION_STATUSES.SUBMITTED_TO_CLEARING
    )
  }

  get isError() {
    return this.status === BANKING_PRE_REGISTRATION_STATUSES.ERROR
  }
}

export const NO_BANKING_PRE_REGISTRATION = new BankingPreRegistration()

export class ExternalTransaction
  extends RestResource
  implements CashTransaction
{
  static onInitialize = (_obj: ExternalTransaction) => {
    /* no-op */
  }

  constructor() {
    super()

    ExternalTransaction.onInitialize(this)
  }

  achRelationship?: AchRelationship
  achRelationshipId = 0
  accountNumber = ""
  bankCountry?: string
  bankingDate?: DateISOString
  direction: TRANSFER_DIRECTIONS = TRANSFER_DIRECTIONS.BLANK
  fundsAvailableDate?: DateISOString
  iraDetail?: IraDetail
  amount = 0
  state: TRANSFER_STATES = TRANSFER_STATES.BLANK
  extTransferId = ""
  reason = ""
  transferMethod: TRANSFER_METHODS = TRANSFER_METHODS.BLANK
  disbursementType = ""
  createdAt: Date | null = null
  updatedAt: Date | null = null
  buyingPowerGrantedAt: Date | null = null
  isCancelable = false
  isClearingAccepted = false
  reversedAt: Date | null = null
  verifiedAt: Date | null = null
  // Stablecoin specific field
  digitalAssetFundConversionId?: string
  recurringExternalTransaction?: RecurringDeposit

  get isRecurring() {
    return !!this.recurringExternalTransaction
  }

  get status() {
    return this.state
  }

  get isAch() {
    return this.transferMethod === TRANSFER_METHODS.ACH
  }

  get isRTP() {
    return this.transferMethod === TRANSFER_METHODS.RTP
  }

  get hasBuyingPowerGranted() {
    return this.buyingPowerGrantedAt !== null
  }

  get isCheck() {
    return this.transferMethod === TRANSFER_METHODS.CHECK
  }

  get isStablecoin() {
    return this.transferMethod === TRANSFER_METHODS.STABLECOIN
  }

  get isMicroDeposit() {
    return (
      this.achRelationship?.achRelationshipMethod ===
      RELATIONSHIP_METHODS.MICRO_DEPOSIT
    )
  }

  get isBuyingPowerGrantEligible() {
    if (this.hasBuyingPowerGranted) {
      return false
    }
    if (!this.isDeposit) {
      return false
    }
    if (!this.isClearingAccepted) {
      return false
    }

    return this.isPending || this.isFundsPosted
  }

  get statusBucket(): TransactionStatusBucket {
    // TODO [LW] - remove once the backend status code "ACTION_REQUIRED" has been added
    if (this.isRTP && this.state === TRANSFER_STATES.PENDING) {
      return TransactionStatusBucket.ACTION_REQUIRED
    }

    return getStatusBucket(this.state)
  }

  get isComplete() {
    return this.state === TRANSFER_STATES.COMPLETE
  }

  get isWire() {
    return this.transferMethod === TRANSFER_METHODS.WIRE
  }

  get isWithdrawal() {
    return this.direction === TRANSFER_DIRECTIONS.OUTGOING
  }

  get isDeposit() {
    return this.direction === TRANSFER_DIRECTIONS.INCOMING
  }

  get isFullBalance() {
    return this.disbursementType === "FULL_BALANCE"
  }

  get isPending() {
    return this.state === TRANSFER_STATES.PENDING
  }

  get isPresend() {
    return this.state === TRANSFER_STATES.PRE_SEND
  }

  get isSendFailed() {
    return this.state === TRANSFER_STATES.SEND_FAILED
  }

  get isSubmissionRejected() {
    return this.isRejected || this.isSendFailed
  }

  get isCanceled() {
    return this.state === TRANSFER_STATES.CANCELED
  }

  get isRejected() {
    return this.state === TRANSFER_STATES.REJECTED
  }

  get isFundsPosted() {
    return this.state === TRANSFER_STATES.FUNDS_POSTED
  }

  get isReturned() {
    return this.state === TRANSFER_STATES.RETURNED
  }

  get isVerified() {
    return !isNil(this.verifiedAt)
  }

  get isProcessed(): boolean {
    return this.isPending || this.isPresend || this.isFundsPosted
  }

  get isActionRequiredStatus() {
    return this.statusBucket === TransactionStatusBucket.ACTION_REQUIRED
  }

  get isProcessingStatus() {
    return this.statusBucket === TransactionStatusBucket.PROCESSING
  }

  get isCompleteStatus() {
    return this.statusBucket === TransactionStatusBucket.COMPLETE
  }

  get isCanceledStatus() {
    return this.statusBucket === TransactionStatusBucket.CANCELED
  }

  get isRejectedStatus() {
    return this.statusBucket === TransactionStatusBucket.REJECTED
  }

  get isReturnedStatus() {
    return this.statusBucket === TransactionStatusBucket.RETURNED
  }

  get isTerminal(): boolean {
    return TERMINAL_TRANSFER_STATES.includes(this.state)
  }

  get isReauthRequired(): boolean {
    return this.reason === TRANSACTION_REAUTH_REQUIRED_REASON
  }

  get isLoginExpired(): boolean {
    return this.achRelationship?.isLoginExpired ?? false
  }
}

export const EXTERNAL_TRANSACTION_DESER: SchemaDeSer<ExternalTransaction> =
  new SchemaDeSerBuilder(ExternalTransaction)
    .ofNested("achRelationship", ACH_RELATIONSHIP_DESER, AchRelationship)
    .ofNested("iraDetail", IRA_DETAIL_DESER, IraDetail)
    .ofString("id")
    .ofInt("achRelationshipId")
    .ofString("accountNumber")
    .ofString("bankingDate")
    .ofString("bankCountry")
    .ofString("direction")
    .ofString("fundsAvailableDate")
    .ofFloat("amount")
    .ofString("state")
    .ofString("extTransferId")
    .ofString("reason")
    .ofString("transferMethod")
    .ofString("disbursementType")
    .ofDateTime("createdAt")
    .ofDateTime("updatedAt")
    .ofDateTime("buyingPowerGrantedAt")
    .ofBoolean("isCancelable")
    .ofBoolean("isClearingAccepted")
    .ofDateTime("verifiedAt")
    .ofDateTime("reversedAt")
    .ofInt("digitalAssetFundConversionId")
    .ofNested(
      "recurringExternalTransaction",
      RECURRING_DEPOSIT_DESER,
      RecurringDeposit
    )
    .toDeSer()

export class AvailableBuyingPowerResponse extends RestResource {
  amount = 0
}

export const AVAILABLE_BUYING_POWER_DESER: SchemaDeSer<AvailableBuyingPowerResponse> =
  new SchemaDeSerBuilder(AvailableBuyingPowerResponse)
    .ofFloat("amount")
    .toDeSer()

export class PendingCashEntry extends RestResource {
  accountNumber = ""
  apexCashActivityId = 0
  amount = 0
  effect = CostEffect.NoCost
  state: PendingCashEntryState = PendingCashEntryState.ACTIVE
  expirationDate = new Date()
  releasedAt = null
  releasedByUserId = 0
  description = ""
  externalTransactionId = 0

  get isDebit() {
    return this.effect === CostEffect.Debit
  }

  get isCredit() {
    return this.effect === CostEffect.Credit
  }

  get isActive() {
    return this.state === PendingCashEntryState.ACTIVE
  }

  get totalCost() {
    return Cost.create(this.amount, this.effect)
  }
}

export interface PlaidBrokerageSearchResult {
  publicToken: string
  metadata: PlaidResultMetadata
}

export interface PlaidResultMetadata {
  accounts: {
    id: string
  }[]
  institution: {
    institution_id: string
    name: string
  }
}

export enum LinkEvents {
  OPEN = "OPEN",
  ERROR = "ERROR",
}

export interface LinkEventMetadata {
  error_type: string
  error_code: string
  error_message: string
}

export class PlaidLinkToken {
  linkToken = ""
  expiration = new Date()
  requestId = ""
  redirectUri?: string

  get isExpired(): boolean {
    return (
      !isEmpty(this.linkToken) &&
      this.expiration.getTime() < new Date().getTime()
    )
  }
}

export const PLAID_LINK_TOKEN_DESER: SchemaDeSer<PlaidLinkToken> =
  new SchemaDeSerBuilder(PlaidLinkToken)
    .ofString("linkToken")
    .ofDateTime("expiration")
    .ofString("requestId")
    .ofString("redirectUri")
    .toDeSer()
